import { Themed } from "@client/styles/themes";
import { useCurrentBreakpoint } from "@client/utils/useCurrentBreakpoint";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { gsap } from "gsap/dist/gsap";
import React from "react";

import { FloatingPromoBlock as Value } from "@reactivated";

import { HeroLayoutSettings } from "@thelabnyc/thelabui/src/components/Flexihero/HeroLayout.interfaces";
import { HeroSet } from "@thelabnyc/thelabui/src/components/Flexihero/HeroSet";

import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichText } from "../RichText";

import styles from "./index.module.scss";

interface Props {
    value: Value;
}

export const FloatingPromoBlock = ({ value }: Props) => {
    const container = React.useRef<HTMLDivElement>(null);
    const currentBreakpoint = useCurrentBreakpoint();

    useGSAP(() => {
        // Clear any existing ScrollTriggers first
        ScrollTrigger.getAll().forEach((st) => {
            if (st.trigger === container.current) {
                st.kill();
            }
        });

        const mediaWrappers = container.current?.querySelectorAll(
            `.${styles.media}`,
        );

        mediaWrappers?.forEach((mediaWrapper) => {
            // floating videos or embeds not supported at this time
            // targeting image because the first attempt targeted mediaWrapper
            // and the height was unreliable which caused questionable animations
            const image = mediaWrapper.querySelector("img");
            const imageHeight = image?.getBoundingClientRect().height;

            if (imageHeight && imageHeight > 0) {
                gsap.to(mediaWrapper, {
                    y: imageHeight,
                    scrollTrigger: { trigger: container.current, scrub: 1 },
                });
                gsap.to(image, {
                    y: -imageHeight,
                    scrollTrigger: { trigger: container.current, scrub: true },
                });
            }
        });
    }, [currentBreakpoint]);

    const content = value.content && (
        <RichText value={value.content} className={styles.text} />
    );
    const media = value.media && (
        <MediaChooserBlock
            value={value.media}
            mediaAttrs={{ className: styles.image }}
            wrapperAttrs={{ className: styles.picture }}
        />
    );

    const settings: HeroLayoutSettings = {
        heroDirection: "row",
        heroPlacement: "container",
        mediaPlacement: "inline",
        mediaAspectRatio: "none",
        contentAlignPrimary: "middle",
        contentAlignSecondary: "left/top",
        textAlignment: "left",
        contentWidth: 7,
    };

    return (
        <Themed ref={container} theme={value.theme} className={styles.root}>
            <HeroSet
                layouts={{
                    desktop: {
                        content,
                        media,
                        settings: {
                            ...settings,
                            gapWidth: 1,
                            mediaWidth: 4,
                        },
                        styles: {
                            contentClassName: styles.contentDesktop,
                            mediaClassName: styles.mediaDesktop,
                        },
                    },
                    tablet: {
                        content,
                        media,
                        settings: {
                            ...settings,
                            gapWidth: 1,
                            mediaWidth: 4,
                        },
                        styles: {
                            contentClassName: styles.contentTablet,
                            mediaClassName: styles.mediaDesktop,
                        },
                    },
                    mobile: {
                        content,
                        media,
                        settings: {
                            ...settings,
                            heroDirection: "column",
                            contentAlignSecondary: "right/bottom",
                            contentWidth: 12,
                        },
                        styles: {
                            contentClassName: styles.contentMobile,
                            mediaClassName: styles.mediaMobile,
                        },
                    },
                }}
            />
        </Themed>
    );
};
